import TableContainer from "@material-ui/core/TableContainer";
import React, { useEffect, useReducer, useState } from "react";
import {
    Button,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import PageHelpGuide from "../../components/PageHelpGuide";
import { SERVICES_HELP_DATA } from "../../constants/pageHelp/servicesHelp";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import ServiceModal from "../../components/ServiceModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    backgroundColor: theme.palette.type === "dark" ? "#2A2D32 !important" : theme.palette.background.paper,
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.type === "dark" ? "#1e2126" : "#f3f4f6",
    color: theme.palette.type === "dark" ? "#ffffff" : "#111827",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    zIndex: 1,
    position: "sticky",
    top: 0,
  },
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_SERVICES") {
        const services = action.payload;
        const newServices = [];

        services.forEach((service) => {
            const serviceIndex = state.findIndex((s) => s.id === service.id);
            if (serviceIndex !== -1) {
                state[serviceIndex] = service;
            } else {
                newServices.push(service);
            }
        });

        return [...state, ...newServices];
    }

    if (action.type === "UPDATE_SERVICES") {
        const service = action.payload;
        const serviceIndex = state.findIndex((s) => s.id === service.id);

        if (serviceIndex !== -1) {
            state[serviceIndex] = service;
            return [...state];
        } else {
            return [service, ...state];
        }
    }

    if (action.type === "DELETE_SERVICE") {
        const serviceId = action.payload;
        const serviceIndex = state.findIndex((s) => s.id === serviceId);
        if (serviceIndex !== -1) {
            state.splice(serviceIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const Services = () => {
    const classes = useStyles();

    const [services, dispatch] = useReducer(reducer, []);
    const [loading, setLoading] = useState(false);
    const [serviceModalOpen, setServiceModalOpen] = useState(false);
    const [selectedService, setSelectedService] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/services");
                dispatch({ type: "LOAD_SERVICES", payload: data });
                setLoading(false);
            } catch (err) {
                toastError(err);
                setLoading(false);
            }
        })();
    }, []);

    const handleOpenServiceModal = () => {
        setSelectedService(null);
        setServiceModalOpen(true);
    };

    const handleCloseServiceModal = () => {
        setServiceModalOpen(false);
        setSelectedService(null);
        loadServices();
    };

    const loadServices = async () => {
        try {
            const { data } = await api.get("/services");
            dispatch({ type: "RESET" });
            dispatch({ type: "LOAD_SERVICES", payload: data });
        } catch (err) {
            toastError(err);
        }
    };

    const handleEditService = (service) => {
        setSelectedService(service);
        setServiceModalOpen(true);
    };

    const handleDeleteService = async (serviceId) => {
        try {
            await api.delete(`/services/${serviceId}`);
            toast.success(i18n.t("serviceModal.toasts.deleted"));
            dispatch({ type: "DELETE_SERVICE", payload: serviceId });
        } catch (err) {
            toastError(err);
        }
        setConfirmModalOpen(false);
    };

    return (
        <MainContainer>
            <ConfirmationModal
                title={selectedService && `${i18n.t("serviceModal.confirmationModal.deleteTitle")} ${selectedService.name}?`}
                open={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={() => handleDeleteService(selectedService.id)}
            >
                {i18n.t("serviceModal.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <ServiceModal
                open={serviceModalOpen}
                onClose={handleCloseServiceModal}
                serviceId={selectedService?.id}
            />
            <Paper className={classes.mainPaper} variant="outlined">
                <MainHeader>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <PageHelpGuide
                            helpData={SERVICES_HELP_DATA}
                            tooltipText="Guía y Ayuda de Servicios (Haz clic para ver)"
                        />
                        <Title>{i18n.t("serviceModal.title.list")} ({services.length})</Title>
                    </div>
                    <MainHeaderButtonsWrapper>
                        <Button
                            className={classes.applyButton}
                            onClick={handleOpenServiceModal}
                        >
                            {i18n.t("common.add")}
                        </Button>
                    </MainHeaderButtonsWrapper>
                </MainHeader>
                <TableContainer className={classes.scrollArea}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeader} align="center">{i18n.t("serviceModal.table.name")}</TableCell>
                                <TableCell className={classes.tableHeader} align="center">{i18n.t("serviceModal.table.description")}</TableCell>
                                <TableCell className={classes.tableHeader} align="center">{i18n.t("serviceModal.table.duration")}</TableCell>
                                <TableCell className={classes.tableHeader} align="center">{i18n.t("serviceModal.table.price")}</TableCell>
                                <TableCell className={classes.tableHeader} align="center">{i18n.t("serviceModal.table.actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {services.map((service) => (
                                <TableRow key={service.id}>
                                    <TableCell align="center">{service.name}</TableCell>
                                    <TableCell align="center">{service.description}</TableCell>
                                    <TableCell align="center">{service.duration} mins</TableCell>
                                    <TableCell align="center">{service.price}</TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditService(service)}
                                        >
                                            <Edit style={{ color: "#00a8ff" }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSelectedService(service);
                                                setConfirmModalOpen(true);
                                            }}
                                        >
                                            <DeleteOutline style={{ color: "#e84118" }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={5} />}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </MainContainer>
    );
};

export default Services;
